import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import { useAccountMenuItems } from '../navigation/useAccountMenuItems';
import ReferVoteMateDialog from '../navigation/ReferVoteMateDialog';
import StoreBadge from '../landing/StoreBadge';
import { useAppStoreLink } from '@/hooks/useAppStoreLink';
import { useDirection } from '@/hooks/useDirection';
import { ltrValueProps } from '@/utils/bidi';
import { semanticColors, BORDER_RADIUS } from '@/theme/tokens';

interface HeaderAccountMenuProps {
  /** Page-level feedback override, forwarded to the shared menu items. */
  onFeedbackClick?: () => void;
}

/**
 * Signed-in avatar in the mobile app bar's top-right corner. Opens the same
 * account menu the left drawer carries at its bottom — support, refer,
 * feedback, help, settings, sign out — as a compact popover, so a voter reaches
 * account actions without first opening the navigation drawer. Desktop keeps
 * the rail avatar as its entry point and does not render this.
 */
export function HeaderAccountMenu({ onFeedbackClick }: HeaderAccountMenuProps) {
  const { t } = useTranslation('common');
  const dir = useDirection();
  const { user, signOut } = useAuth();
  const { track } = useAnalytics();
  const menuId = useId();
  const appStore = useAppStoreLink();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const open = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);

  const items = useAccountMenuItems({
    source: 'header_account_menu',
    onDismiss: closeMenu,
    onRefer: () => setShareOpen(true),
    onFeedbackClick,
  });

  // Nickname for the menu header: display name, or the local part of either the
  // name-as-email or the email itself (same rule as the drawer's account row).
  const accountDisplayName =
    (user?.name?.includes('@') ? user?.name?.split('@')[0] : user?.name) ||
    user?.email?.split('@')[0];

  return (
    <>
      <IconButton
        // Class opts this button out of the layout's blanket hide rules for
        // Toolpad's own "…menu" toolbar buttons.
        className="vm-header-account"
        data-settings-return-focus="account"
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          track('Account Menu Opened', { source: 'header_avatar', guest: false });
        }}
        aria-label={t('accountMenu')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        sx={{
          p: '3px',
          // Sit on the same line as the language chip beside it. Doubled
          // specificity (``&&``) beats the Toolpad toolbar Stack's child-margin
          // reset, as the Sign In pill does.
          '&&': { marginTop: { xs: '6px', md: '8px' }, marginRight: { xs: '2px', md: '8px' } },
          '&:focus-visible': {
            outline: `2px solid ${semanticColors.brandPrimary}`,
            outlineOffset: '2px',
          },
        }}
      >
        <Avatar
          src={user?.picture || undefined}
          alt=""
          sx={{
            width: 30,
            height: 30,
            fontSize: '13px',
            fontWeight: 600,
            bgcolor: semanticColors.textPrimary,
            color: semanticColors.textOnPrimary,
          }}
        >
          {accountDisplayName?.charAt(0)?.toUpperCase()}
        </Avatar>
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Above the ballot sheet (theme.zIndex.modal + 1), else it opens hidden
        // behind the sheet when the ballot is up.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        slotProps={{
          paper: {
            sx: {
              mt: '6px',
              minWidth: 248,
              borderRadius: BORDER_RADIUS,
              boxShadow: `0 8px 24px ${semanticColors.shadowLight}, 0 1px 3px ${semanticColors.shadowLight}`,
            },
          },
          list: { 'aria-label': t('accountMenu'), sx: { py: '4px' } },
        }}
      >
        <Box sx={{ px: 2, pt: '10px', pb: '8px' }}>
          {accountDisplayName && (
            <Typography
              noWrap
              sx={{ fontWeight: 600, fontSize: '14px', color: semanticColors.textBody }}
            >
              {accountDisplayName}
            </Typography>
          )}
          {user?.email && (
            <Typography
              noWrap
              {...ltrValueProps(dir)}
              sx={{ fontSize: '12px', color: semanticColors.textTertiary }}
            >
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider sx={{ opacity: 0.3 }} />
        {appStore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1.25 }}>
            <StoreBadge
              store={appStore.os}
              href={appStore.href}
              onClick={() => {
                track('App Download Clicked', { source: 'account_menu', os: appStore.os });
                closeMenu();
              }}
            />
          </Box>
        )}
        {items.map(({ key, label, icon, onClick }) => (
          <MenuItem key={key} onClick={onClick} sx={{ gap: '10px', px: 2, py: 1.25 }}>
            {icon}
            <Typography sx={{ fontSize: '14px', color: semanticColors.textBody }}>
              {label}
            </Typography>
          </MenuItem>
        ))}
        <Divider sx={{ opacity: 0.3 }} />
        <MenuItem
          onClick={() => {
            closeMenu();
            signOut();
          }}
          sx={{ gap: '10px', px: 2, py: 1.25 }}
        >
          <LogoutIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />
          <Typography sx={{ fontSize: '14px', color: semanticColors.textBody }}>
            {t('signOut')}
          </Typography>
        </MenuItem>
      </Menu>

      <ReferVoteMateDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </>
  );
}
